<template>
    <div>
        <h1 class="display-heading mb-3">Register New Patient</h1>

        <form @submit.prevent="validate">
            <div class="form-group">
                <label>Designation</label>
                <select name="designation" data-vv-validate-on="blur" data-vv-as="designation"
                    v-model="form.designationChoice" :class="{
                    'form-control': true,
                    'is-invalid':  validationErrors.has('designation')
                }">
                    <option v-for="(designation, index) in designations" :key="index"
                        :value="designation.value">
                        {{designation.text}}
                    </option>
                </select>

                <input v-if="form.designationChoice === -1" type="text" name="designationText" data-vv-validate-on="blur" data-vv-as="designation"
                    placeholder="Designation" v-model="form.designation" v-validate="'required'" :class="{
                    'form-control': true,
                    'is-invalid':  validationErrors.has('designationText')
                }">

                <div v-show="validationErrors.has('designation')" class="invalid-feedback">
                    {{ validationErrors.first('designation') }}
                </div>
            </div>

            <div class="form-group">
                <label>Full Name (Must be exactly as appeared in IC)</label>
                <input type="text" name="fullName" data-vv-validate-on="blur" data-vv-as="full name"
                    placeholder="Full Name" v-model="form.name" v-validate="'required'" :class="{
                    'form-control': true,
                    'is-invalid':  validationErrors.has('fullName')
                }">
                <div v-show="validationErrors.has('fullName')" class="invalid-feedback">
                    {{ validationErrors.first('fullName') }}
                </div>
            </div>

            <div class="form-group">
                <label>Identification Type</label>
                <select name="identificationType" data-vv-validate-on="blur" data-vv-as="identification type"
                    v-validate="identificationTypeValidation" v-model="form.identificationType" :class="{
                    'form-control': true,
                    'is-invalid':  validationErrors.has('identificationType')
                }">
                    <option value="0">Please Select</option>
                    <option v-for="(identificationType, index) in identificationTypes" :key="index"
                        :value="identificationType.value">
                        {{identificationType.text}}
                    </option>
                </select>
                <div v-show="validationErrors.has('identificationType')" class="invalid-feedback">
                    {{ validationErrors.first('identificationType') }}
                </div>
            </div>

            <div class="form-group">
                <label>Identification Number</label>
                <input type="text" name="identityNumber" data-vv-validate-on="blur"
                    data-vv-as="identification number" placeholder="Identification Number"
                    v-model="form.identificationNumber" v-mask="form.identificationType === 1 ? '######-##-####' : null" v-validate="'required'" :class="{
                    'form-control': true,
                    'is-invalid':  validationErrors.has('identityNumber')
                }">
                <div v-show="validationErrors.has('identityNumber')" class="invalid-feedback">
                    {{ validationErrors.first('identityNumber') }}
                </div>
            </div>

            <div class="form-group">
                <label>Phone Number</label>
                <input type="text" name="mobileNumber" data-vv-validate-on="blur" data-vv-as="phone number"
                    placeholder="Phone Number" v-model="form.mobileNumber" v-validate="'required'" :class="{
                    'form-control': true,
                    'is-invalid':  validationErrors.has('mobileNumber')
                }">
                <div v-show="validationErrors.has('mobileNumber')" class="invalid-feedback">
                    {{ validationErrors.first('mobileNumber') }}
                </div>
            </div>

            <div class="form-group">
                <label>Email Address (Optional, but useful for viewing future personal medical records)</label>
                <input v-if="!emailReadonly" type="text" name="email" v-model="form.email" :class="{
                    'form-control': true,
                    'is-invalid':  validationErrors.has('email')
                }">
                <div v-else class="mb-2">
                    <b>{{emailAddressDisplay}}</b>
                </div>
                <div v-show="validationErrors.has('email')" class="invalid-feedback">
                    {{ validationErrors.first('email') }}
                </div>
            </div>
        </form>

        <p v-if="formHasError" class="text-danger">
            {{formErrorMessage}}
        </p>

        <button type="button" class="btn btn-primary btn-primary-insv mr-2" @click="registerPatient">
            Register Now
        </button>

        <popup-modal ref="popupModalSuccess" :hasCloseButton="false" :title="registerSuccess ? 'Registration Complete!' : 'Registering Your New Patient'">
            <template slot="content">
                <template v-if="registerSuccess">
                    <div>
                        <p>Patient registered!</p>
                        <p v-if="form.email">Email for getting full user profile just sent out!</p>
                        <p>Please click 'Okay' to continue.</p>
                    </div>
                </template>
                <template v-else>
                    <p>Please wait while we are processing your registration...</p>
                </template>
            </template>
            <template v-if="registerSuccess" slot="footer">
                <button @click="redirect" class="btn btn-primary btn-primary-insv">
                    Okay
                </button>
            </template>
        </popup-modal>

        <popup-modal ref="popupRegistrationError" :hasCloseButton="false" title="Error Creating New User">
            <template slot="content">
                <p>{{createUser.errorMessage}}</p>
                <ul>
                    <li v-for="(error, key) in createUser.errors" :key="key">
                        {{error}}
                    </li>
                </ul>
            </template>
            <template slot="footer">
                <button @click="dismissCreateError" class="btn btn-primary btn-primary-insv">Try Again</button>
            </template>
        </popup-modal>
    </div>
</template>

<script>
import {
    Validator
} from 'vee-validate';
import popupModal from '@/components/popup-modal';
import identificationTypes from '@/config/identificationTypes';
import designations from '@/config/designations';
import { VueMaskDirective } from 'v-mask';

export default {
    'name': 'RegisterPatientComponent',
    'directives': {
        'mask': VueMaskDirective
    },
    'components': {
        popupModal
    },
    data () {
        return {
            'form': {
                'designation': '',
                'designationChoice': 'Mr.',
                'name': '',
                'mobileNumber': '',
                'identificationType': '',
                'identificationNumber': '',
                'email': ''
            },
            'emailReadonly': null,
            'emailAddressDisplay': null,
            'formHasError': false,
            'formErrorMessage': '',
            'registerSuccess': false,
            'createUser': {
                'errorMessage': '',
                'errors': []
            },
            identificationTypes,
            designations
        };
    },

    'computed': {
        identificationTypeValidation () {
            let minIdentificationType = this.identificationTypes[0].value;
            let maxIdentificationType = this.identificationTypes[this.identificationTypes.length - 1].value;

            return `required|between:${minIdentificationType},${maxIdentificationType}`;
        }
    },

    mounted () {
        const dict = {
            'custom': {
                'gender': {
                    'between': 'Gender is required.'
                },
                'identificationType': {
                    'between': 'Identification type is required.'
                }
            }
        };
        Validator.localize('en', dict);
        this.$validator.localize('en', dict);

        if (!this.form.identificationType && this.identificationTypes.length > 0) {
            this.form.identificationType = this.identificationTypes[0].value;
        }
    },
    'methods': {
        validate () {
            return this.$validator.validateAll();
        },
        registerPatient () {
            this.validate().then((isValid) => {
                if (isValid) {
                    this.$refs.popupModalSuccess.show().then(() => {
                        this.registerSuccess = false;
                        this.formHasError = false;

                        let formData = this.createUserDataForm();

                        this.axios.post('Clinic/RegisterPatient', formData).then(({
                            data
                        }) => {
                            this.registerSuccess = true;
                        }).catch((error) => {
                            if (error && error.response && error.response.data) {
                                this.createUser.errorMessage = error.response.data.message;
                                let errorList = this.spreadRegistrationErrors(error.response
                                    .data.data && error.response.data.data.ve ? error
                                        .response.data.data.ve : []);

                                this.createUser.errors = errorList;
                            } else {
                                this.createUser.errorMessage = 'An error has occured. Please try again.';
                            }
                            this.$refs.popupModalSuccess.hide().then(() => {
                                this.$refs.popupRegistrationError.show();
                            });
                        });
                    });
                } else {
                    this.formHasError = true;

                    let errorFields = this.$refs.formContainer.querySelectorAll(
                        '.is-invalid');

                    if (errorFields.length > 0) {
                        errorFields[0].focus();
                        this.formErrorMessage = `There's error(s) in the form. Please check the form and try again.`;
                    } else {
                        this.formErrorMessage = `Please confirm that you have read and understood all of our Terms and Conditions and Privacy Policy before continue.`;
                    }
                }
            });
        },
        createUserDataForm () {
            let formData = new FormData();

            if (this.form.designationChoice !== -1) {
                this.form.designation = this.form.designationChoice;
            }

            formData.append('FullName', this.form.name);
            formData.append('Designation', this.form.designation);
            formData.append('PhoneNumber', this.form.mobileNumber);
            formData.append('IdentityType', this.form.identificationType);
            formData.append('IdentityCardNumber', this.form.identificationNumber);
            formData.append('Email', this.form.email);
            return formData;
        },
        redirect () {
            this.$refs.popupModalSuccess.hide().then(() => {
                this.$router.push({
                    'name': 'clinic-check-in-registered-patient',
                    'query': {
                        'ic': this.form.identificationNumber
                    }
                });
            });
        },
        dismissCreateError () {
            this.$refs.popupRegistrationError.hide();
        },
        spreadRegistrationErrors (errors) {
            let errorList = [];

            let firstLayerErrors = errors.map(e => e.e);

            firstLayerErrors.forEach(err => {
                if (err.length > 0) {
                    errorList.push(...err);
                }
            });

            return errorList;
        }
    }
};

</script>
